import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { FullPageLoading, LoadingSpinner } from '../../components/loading'
import { routes } from '../../router'
import ApiClient from '../../lib/api-client'
import SignoutButton from '../../components/buttons/signout-button'
import useActionRequired from '../../hooks/use-action-required'
import EulaModal from './eula-modal'
import useLoginUrl, { postAuthenticate } from '../../hooks/use-login-url'
import getClientPortalUrl from '../../lib/get-client-portal-url'
import { setNotifyOnce } from '../../hooks/use-notify-once'
import GenericError from './generic-error'
import ReadMore from './read-more'
import RegionSelector from '../../components/region-selector'

function AuthPage () {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState()
  const [helpSearch, setHelpSearch] = useState()
  const [isGoLogin, setIsGoLogin] = useState(false)
  const [hasToken, setHasToken] = useState(false)
  const [nextRoute, setNextRoute] = useState(() => routes.dashboard())
  const [showEula, setShowEula] = useState(false)
  const [newPortalUrl, setNewPortalUrl] = useState(getClientPortalUrl())
  const query = useActionRequired(hasToken)
  const urlQuery = useLoginUrl(newPortalUrl)
  const hasCalledAuthenticate = useRef(false)

  useEffect(() => {
    const error = searchParams.get('error')
    const code = searchParams.get('code')
    const data = searchParams.get('data')
    const helpSearch = searchParams.get('helpSearch')
    let dataParams

    if (helpSearch) {
      setHelpSearch(helpSearch)
    }

    if (data) {
      dataParams = new URLSearchParams(window.atob(data))
    }

    const oAuthCallback = async () => {
      // NOTE: When authentication error from ArcGIS, query params will includes
      //   "data": "......"
      //   "error": "invalid_request",
      //   "error_description": "Access to this app...",
      //   "messageCode": "OAUTH_0075"

      const result = await postAuthenticate({ code, data })
      if (result.error) {
        navigate(`/auth?error=${error.toString()}`, { replace: true })
        return
      }

      if (result.data?.error) {
        const qs = new URLSearchParams(result.data)
        qs.delete('status')
        navigate(`/auth?${qs.toString()}`, { replace: true })
        return
      }

      if (result.data?.jwt && result.data?.refreshToken) {
        //  Login success / New session begins

        // Reset flag for showing notifications that appears only once
        setNotifyOnce()

        // Save new token
        ApiClient.setRefreshToken(result.data.refreshToken)
        ApiClient.setToken(result.data.jwt)
        setHasToken(true)
      }

      if (result.data?.portalUrl) {
        window.localStorage.setItem('instalinkPortalUrl', result.data.portalUrl)
      }

      if (result.data?.regionCode) {
        window.localStorage.setItem('instalinkRegionCode', result.data.regionCode)
      }

      if (result.data?.next) {
        // NOTE: At this point, the login has succeed. Update the nextRoute state and
        // actionRequired hook will runs and handle the redirect to next route.
        setNextRoute(result.data.next)
      }
    }

    if (code && data && !hasCalledAuthenticate.current) {
      hasCalledAuthenticate.current = true
      oAuthCallback()
        .catch(error => {
          console.error('Error in /auth-callback:', error)
          navigate('/auth?error=Server Error', { replace: true })
        })
      return
    }

    if (error === 'access_denied') {
      const setupCode = dataParams?.get('setup')
      const returnPath = dataParams?.get('returnPath')
      if (setupCode && returnPath) {
        // Handle user cancelled login during onboarding
        navigate(`${returnPath}?code=${setupCode}`, { replace: true })
      } else {
        navigate(nextRoute, { replace: true })
      }
      return
    }

    if (error) {
      return setError(error)
    }
    // eslint-disable-next-line
  }, [searchParams])

  useEffect(() => {
    if (query.isLoading || !query.data) {
      return
    }

    const actionRequired = query.data.actionRequired || []
    if (actionRequired.length === 0) {
      // No pending task, proceed to next
      setTimeout(() => {
        navigate(nextRoute, { replace: true })
      }, 0)
    }
    for (const action of actionRequired) {
      if (action.configType === 'EULA_FEB_24') {
        setShowEula(true)
        break
      }
    }
  }, [navigate, query.isLoading, query.data, nextRoute])

  useEffect(() => {
    if (!isGoLogin || urlQuery.isLoading || !urlQuery.data) {
      return
    }
    setTimeout(() => {
      window.location.href = urlQuery.data
    }, 0)
  }, [urlQuery.isLoading, urlQuery.data, isGoLogin])

  const handleGo = () => {
    const value = document.getElementById('inputPortalUrl').value
    if (value !== newPortalUrl) {
      setNewPortalUrl(value)
      setIsGoLogin(true)
    } else {
      setTimeout(() => {
        if (urlQuery.data) {
          window.location.href = urlQuery.data
        }
      }, 0)
    }
  }

  if (error === 'subscription-expired') {
    return (
      <GenericError
        errorMessage={t('authPage_subscriptionExpiredText')}
      />
    )
  }

  if (error === 'user-limit-exceeded') {
    return (
      <GenericError
        errorMessage={t('authPage_userLimitExceededText')}
      />
    )
  }

  if (error === 'unauthorized-access-arcgis-portal') {
    return (
      <GenericError errorMessage={t('authPage_portalErrorText')}>
        <div className='mt-8 text-white'>
          <p>{t('authPage_portalErrorLabel')}</p>
          <Trans t={t} i18nKey='authPage_portalErrorDetails'>
            <ul className='ml-2 p-2 list-disc'>
              <li />
              <li />
            </ul>
          </Trans>
        </div>
        <ReadMore error={error} helpSearch={helpSearch} />
      </GenericError>
    )
  }

  if (error) {
    const messageCode = searchParams.get('messageCode')
    const showInput = error === 'missing-code-param' ||
      error === 'missing-portal-url' ||
      error === 'missing-data-param' ||
      error === 'invalid-tenant-account' ||
      error === 'change-portal-url'

    if (showInput) {
      return (
        <GenericError error={error} errorMessage={t('authPage_defaultErrorText')} flipped={error !== 'change-portal-url'}>
          <ReadMore error={error} helpSearch={helpSearch} />
          <div className='mt-12 my-5 text-white leading-5'>
            <RegionSelector
              label={t('loginPage_selectRegionLabel')}
              redirectPath='/auth'
            />
          </div>
          <p>
            <label className='block text-white'>
              {t('authPage_portalUrlLabel')}
            </label>
            <input
              id='inputPortalUrl'
              className='mt-2 w-full border border-gray-50 px-4 py-[11px] leading-5'
              type='url'
              name='portalUrl'
              defaultValue={newPortalUrl}
            />
            <span className='text-sm text-white'>{urlQuery.error?.message}</span>
          </p>

          <button
            type='button'
            className='mt-8 w-72 text-center py-5 inline-block bg-yellow-20 font-bold'
            onClick={handleGo}
          >
            {urlQuery.isLoading
              ? <LoadingSpinner className='h-3.5' />
              : t('glob_loginButtonLabel')}
          </button>
        </GenericError>
      )
    }

    let showSignout = error === 'invalid-role-user'
    let errorMessage = searchParams.get('error_description') ?? searchParams.get('error')

    if (error === 'unlicensed') {
      errorMessage = t('authPage_unlicensedText')
      showSignout = true
    }

    if (messageCode) {
      errorMessage = `${errorMessage} (${messageCode})`
    }

    return (
      <GenericError errorMessage={errorMessage}>
        <ReadMore error={error} helpSearch={helpSearch} />
        <div className='pt-12'>
          {showSignout && (
            <SignoutButton className='flex btn-primary' logoutPath='/' />
          )}
        </div>
      </GenericError>
    )
  }

  const handleEulaAccepted = () => {
    setShowEula(false)
  }

  return (
    <>
      <FullPageLoading />
      <EulaModal isOpen={showEula} onSuccess={handleEulaAccepted} />
    </>
  )
}

export default AuthPage
